import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Loader2, Send, AlertTriangle, CheckCircle2, X, Download, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import QuickEditRoss1000Dialog from './QuickEditRoss1000Dialog';

const REASON_LABELS = {
  delega_non_attiva: { label: 'Delega ROSS1000 non attiva', help: 'Disattiva il toggle "Delega ROSS1000/ISTAT" se l\'immobile non va inviato, oppure attiva la delega.' },
  mode_non_impostato: { label: 'Tipo credenziali non scelto', help: 'Seleziona "Account di Gruppo" (provincia) oppure "Credenziali Individuali" nella scheda immobile.' },
  provincia_non_riconosciuta: { label: 'Provincia non riconosciuta per account di gruppo', help: 'La provincia non corrisponde a una provincia con account di gruppo configurato.' },
  account_gruppo_assente_GE: { label: 'Account di gruppo Genova mancante', help: 'Aggiungi le credenziali Genova in "Credenziali di Gruppo".' },
  account_gruppo_assente_IM: { label: 'Account di gruppo Imperia mancante', help: 'Aggiungi le credenziali Imperia in "Credenziali di Gruppo".' },
  account_gruppo_assente_SP: { label: 'Account di gruppo La Spezia mancante', help: 'Aggiungi le credenziali La Spezia in "Credenziali di Gruppo".' },
  account_gruppo_assente_SV: { label: 'Account di gruppo Savona mancante', help: 'Aggiungi le credenziali Savona in "Credenziali di Gruppo".' },
  credenziali_incomplete: { label: 'Credenziali individuali incomplete', help: 'Inserisci username e password individuali nella scheda immobile.' },
  structure_code_vuoto: { label: 'Codice struttura ROSS1000 mancante', help: 'Inserisci il codice struttura assegnato dal portale ROSS1000.' },
  non_configurato: { label: 'Non configurato', help: 'Configura la sezione ROSS1000 nella scheda immobile.' },
};

/**
 * Component for ROSS1000 bulk send.
 * Props:
 *   - month, year: current selected period
 *   - availableRegions: string[] of regions visible in dashboard
 *   - onCompleted: callback when bulk send finishes
 */
const IstatBulkSendButton = ({ month, year, availableRegions = [], onCompleted, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]); // [] = all
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [job, setJob] = useState(null); // currently running/completed job
  const [expandedReasons, setExpandedReasons] = useState({}); // { reason: bool }
  const [editingProp, setEditingProp] = useState(null); // property being edited
  const [launchError, setLaunchError] = useState(null);
  const pollTimer = useRef(null);

  const monthName = new Date(year, month - 1, 1).toLocaleDateString('it-IT', {
    month: 'long',
    year: 'numeric',
  });

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const fetchPreview = async (regions) => {
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const params = new URLSearchParams({
        month: String(month),
        year: String(year),
      });
      if (regions && regions.length > 0) params.append('regions', regions.join(','));
      const res = await axios.get(`${API}/api/istat/bulk-send/preview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error('Errore caricamento preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setJob(null);
    setSelectedRegions([]);
    fetchPreview([]);
  };

  const toggleRegion = (region) => {
    let next;
    if (selectedRegions.includes(region)) {
      next = selectedRegions.filter((r) => r !== region);
    } else {
      next = [...selectedRegions, region];
    }
    setSelectedRegions(next);
    fetchPreview(next);
  };

  const startPolling = (jobId) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('hw_token');
        const res = await axios.get(`${API}/api/istat/bulk-send/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setJob(res.data);
        if (['completed', 'failed'].includes(res.data.status)) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          if (onCompleted) onCompleted();
        }
      } catch (err) {
        console.error('Polling error', err);
      }
    }, 3000);
  };

  const launchBulkSend = async () => {
    setLaunchError(null);
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/istat/bulk-send`,
        {
          month,
          year,
          regions: selectedRegions.length > 0 ? selectedRegions : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const jobId = res.data.job_id;
      toast.success('Invio massivo avviato in background');
      setJob({
        id: jobId,
        status: 'queued',
        total_properties: 0,
        processed_properties: 0,
        records_sent: 0,
        days_skipped_already_sent: 0,
        errors_count: 0,
      });
      startPolling(jobId);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Errore avvio invio massivo';
      setLaunchError(msg);
      toast.error(msg);
    }
  };

  const cleanupAndRelaunch = async () => {
    if (!window.confirm(
      "🧹 Pulizia intelligente delle submissions:\n\n" +
      "✅ MANTENGO: i giorni realmente vuoti (nessun arrivo/partenza) e i giorni con movimenti reali già inviati.\n" +
      "❌ CANCELLO SOLO: giorni con records=0 ma che AVEVANO movimenti reali (= falsi positivi del bug).\n\n" +
      "Poi rilancio il bulk send. Continuare?"
    )) return;
    try {
      const token = localStorage.getItem('hw_token');
      const params = new URLSearchParams({ month: String(month), year: String(year), smart: 'true' });
      if (selectedRegions.length > 0) params.append('regions', selectedRegions.join(','));
      const cleanupRes = await axios.post(
        `${API}/api/istat/cleanup-empty-submissions?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(cleanupRes.data?.message || 'Cleanup completato');
      // Reload preview then relaunch
      await fetchPreview(selectedRegions);
      setTimeout(launchBulkSend, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore cleanup');
    }
  };

  const cancelJob = async () => {
    if (!job?.id) return;
    if (!window.confirm("Sei sicuro di voler annullare l'invio in corso?")) return;
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/istat/bulk-send/cancel/${job.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Annullamento richiesto');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore annullamento');
    }
  };

  const downloadErrorsCsv = () => {
    if (!job?.errors || job.errors.length === 0) return;
    const header = 'Immobile;Regione;Data;Errore\n';
    const rows = job.errors
      .map((e) =>
        [
          (e.property_name || '').replaceAll(';', ','),
          (e.region || '').replaceAll(';', ','),
          e.date || '',
          (e.error || '').replaceAll(';', ',').replaceAll('\n', ' '),
        ].join(';')
      )
      .join('\n');
    const skipRows = (job.skipped_no_credentials || [])
      .map((s) =>
        [
          (s.property_name || '').replaceAll(';', ','),
          (s.region || '').replaceAll(';', ','),
          'TUTTI',
          (`SALTATO: ${s.reason}`).replaceAll(';', ','),
        ].join(';')
      )
      .join('\n');
    const csv = header + rows + (skipRows ? '\n' + skipRows : '');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_send_errors_${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isRunning = job && ['queued', 'running'].includes(job.status);
  const isCompleted = job && job.status === 'completed';
  const progressPct = job?.total_properties > 0
    ? Math.round((job.processed_properties / job.total_properties) * 100)
    : 0;

  return (
    <>
      <Button
        onClick={() => { if (!disabled) handleOpen(); }}
        disabled={disabled}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        data-testid="bulk-send-trigger-btn"
      >
        <Send className="h-4 w-4 mr-1" />
        Invio Massivo Mese
      </Button>

      <Dialog open={open} onOpenChange={(v) => !isRunning && setOpen(v)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Invio Massivo ROSS1000 — {monthName}
            </DialogTitle>
            <DialogDescription>
              Verranno inviati solo gli immobili con credenziali configurate. I giorni già inviati
              con successo verranno saltati automaticamente.
            </DialogDescription>
          </DialogHeader>

          {!job && (
            <div className="space-y-4">
              {/* Region multi-select */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">
                    Regioni{' '}
                    <span className="text-xs text-slate-500">
                      ({selectedRegions.length === 0
                        ? 'Tutte'
                        : `${selectedRegions.length} selezionate`})
                    </span>
                  </label>
                  {selectedRegions.length > 0 && (
                    <button
                      onClick={() => {
                        setSelectedRegions([]);
                        fetchPreview([]);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                      data-testid="bulk-send-clear-regions"
                    >
                      Tutte le regioni
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-44 overflow-y-auto p-2 border rounded-lg">
                  {availableRegions.map((r) => (
                    <label
                      key={r}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded"
                    >
                      <Checkbox
                        checked={selectedRegions.includes(r)}
                        onCheckedChange={() => toggleRegion(r)}
                        data-testid={`region-checkbox-${r}`}
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview stats */}
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="pt-4">
                  {previewLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : preview ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Immobili in scope:</span>
                        <strong>{preview.total_properties_in_scope}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-emerald-700">
                          Immobili pronti per invio:
                        </span>
                        <strong className="text-emerald-700">
                          {preview.sendable_properties}
                        </strong>
                      </div>
                      <div className="flex justify-between text-amber-700">
                        <span>Saltati per credenziali mancanti:</span>
                        <span>
                          {preview.total_properties_in_scope - preview.sendable_properties}
                        </span>
                      </div>
                      {preview.not_sendable_reasons &&
                        Object.keys(preview.not_sendable_reasons).length > 0 && (
                          <div className="ml-2 mt-2 space-y-1.5">
                            {Object.entries(preview.not_sendable_reasons).map(([reasonKey, count]) => {
                              const list = (preview.not_sendable_details || {})[reasonKey] || [];
                              const labelInfo = REASON_LABELS[reasonKey] || { label: reasonKey, help: '' };
                              const isExp = !!expandedReasons[reasonKey];
                              return (
                                <div key={reasonKey} className="border rounded bg-white">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedReasons((prev) => ({ ...prev, [reasonKey]: !prev[reasonKey] }))
                                    }
                                    className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 text-xs"
                                    data-testid={`expand-reason-${reasonKey}`}
                                  >
                                    <span className="flex items-center gap-1">
                                      {isExp ? (
                                        <ChevronDown className="h-3 w-3 text-slate-400" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3 text-slate-400" />
                                      )}
                                      <strong>{labelInfo.label}</strong>
                                    </span>
                                    <span className="text-slate-500 font-mono">{count}</span>
                                  </button>
                                  {isExp && (
                                    <div className="border-t bg-slate-50 max-h-44 overflow-y-auto">
                                      {labelInfo.help && (
                                        <div className="px-2 py-1 text-[11px] text-slate-500 border-b bg-amber-50/50">
                                          💡 {labelInfo.help}
                                        </div>
                                      )}
                                      {list.length === 0 && (
                                        <div className="p-2 text-[11px] text-slate-400">
                                          Nessun dettaglio disponibile
                                        </div>
                                      )}
                                      {list.map((p) => (
                                        <div
                                          key={p.id}
                                          className="flex items-center justify-between px-2 py-1.5 border-b last:border-0 text-[11px] hover:bg-white"
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{p.name}</div>
                                            <div className="text-slate-500 truncate">
                                              {p.region}{p.city ? ` · ${p.city}` : ''}{p.province ? ` (${p.province})` : ''}
                                              {p.property_code ? ` · ${p.property_code}` : ''}
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => setEditingProp(p.id)}
                                            className="ml-2 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
                                            data-testid={`edit-skipped-${p.id}`}
                                          >
                                            <Pencil className="h-3 w-3" />
                                            Sistema
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {/* Bulk export CSV of all skipped */}
                            <button
                              type="button"
                              onClick={() => {
                                const all = Object.entries(preview.not_sendable_details || {})
                                  .flatMap(([reason, props]) => props.map(p => ({ ...p, reason })));
                                if (all.length === 0) return;
                                const header = 'Nome;Regione;Provincia;Citta;Codice;Motivo\n';
                                const csv = header + all.map(p => [
                                  (p.name||'').replace(/;/g, ','),
                                  p.region||'', p.province||'', p.city||'', p.property_code||'', p.reason
                                ].join(';')).join('\n');
                                const blob = new Blob([csv], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `istat_saltati_${year}-${String(month).padStart(2,'0')}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                              className="w-full text-[11px] text-blue-600 hover:bg-blue-50 rounded py-1 flex items-center justify-center gap-1"
                              data-testid="export-skipped-csv"
                            >
                              <Download className="h-3 w-3" />
                              Esporta CSV completo immobili saltati
                            </button>
                          </div>
                        )}
                      <hr className="my-2" />
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          Giorni nel mese × immobili:
                        </span>
                        <strong>{preview.max_potential_calls}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Già inviati con successo:</span>
                        <strong>{preview.already_sent}</strong>
                      </div>
                      <div className="flex justify-between text-blue-700 font-semibold">
                        <span>Chiamate stimate:</span>
                        <span>{preview.estimated_calls_to_make}</span>
                      </div>
                      <div className="flex justify-between text-blue-700">
                        <span>Tempo stimato:</span>
                        <span>~{preview.estimated_minutes} minuti</span>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 items-start text-xs text-amber-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  L'invio gira in background. Puoi chiudere la finestra e tornare a controllarlo
                  più tardi. Solo un job alla volta è permesso.
                </span>
              </div>

              {launchError && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="bulk-send-launch-error">
                  <div className="flex items-start gap-2">
                    <X className="h-4 w-4 mt-0.5 flex-shrink-0 text-rose-600" />
                    <div className="flex-1">
                      <div className="font-semibold">Errore avvio invio</div>
                      <div className="mt-1">{launchError}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Progress while running */}
          {isRunning && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="font-medium">
                  {job.status === 'queued' ? 'In coda…' : job.status === 'cancelling' ? 'Annullamento in corso…' : 'Invio in corso…'}
                </span>
              </div>
              {job.total_properties > 0 && (
                <>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="text-sm text-slate-600">
                    {job.processed_properties}/{job.total_properties} immobili processati ({progressPct}%)
                  </div>
                </>
              )}
              {/* Live current item */}
              {(job.current_property_name || job.current_date) && (
                <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2" data-testid="bulk-send-current">
                  🔄 In lavorazione: <strong>{job.current_property_name || '—'}</strong>
                  {job.current_date ? <span className="text-slate-500"> · giorno {job.current_date}</span> : null}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-emerald-50 p-2 rounded">
                  <div className="text-emerald-700 font-bold text-lg">
                    {job.records_sent || 0}
                  </div>
                  <div>Record inviati</div>
                </div>
                <div className="bg-slate-100 p-2 rounded">
                  <div className="text-slate-700 font-bold text-lg">
                    {job.days_skipped_already_sent || 0}
                  </div>
                  <div>Giorni skippati</div>
                </div>
                <div className="bg-rose-50 p-2 rounded">
                  <div className="text-rose-700 font-bold text-lg">
                    {job.errors_count || 0}
                  </div>
                  <div>Errori</div>
                </div>
              </div>
            </div>
          )}

          {/* Final results */}
          {isCompleted && (
            <div className="space-y-3" data-testid="bulk-send-completed-panel">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex gap-2 items-start">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-emerald-800">Invio massivo completato</div>
                  <div className="text-emerald-700 mt-1">{job.summary_message}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                <div className="bg-emerald-50 p-3 rounded">
                  <div className="text-emerald-700 font-bold text-xl">{job.records_sent}</div>
                  <div className="text-xs">Record inviati</div>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <div className="text-blue-700 font-bold text-xl">
                    {job.processed_properties}
                  </div>
                  <div className="text-xs">Immobili</div>
                </div>
                <div className="bg-slate-100 p-3 rounded">
                  <div className="text-slate-700 font-bold text-xl">
                    {job.days_skipped_already_sent || 0}
                  </div>
                  <div className="text-xs">Giorni skippati</div>
                </div>
                <div className="bg-rose-50 p-3 rounded">
                  <div className="text-rose-700 font-bold text-xl">
                    {job.errors_count || 0}
                  </div>
                  <div className="text-xs">Errori</div>
                </div>
              </div>

              {job.errors_count > 0 && job.errors && (
                <div>
                  <div className="font-medium text-sm mb-1 text-rose-700">
                    Primi {Math.min(10, job.errors.length)} errori:
                  </div>
                  <div className="border rounded-lg max-h-44 overflow-y-auto text-xs">
                    {job.errors.slice(0, 10).map((e, idx) => (
                      <div
                        key={idx}
                        className="p-2 border-b last:border-b-0 flex justify-between gap-2"
                      >
                        <div>
                          <strong>{e.property_name}</strong>{' '}
                          <span className="text-slate-500">({e.region})</span>{' '}
                          <span className="text-slate-400">— {e.date}</span>
                        </div>
                        <div className="text-rose-600 truncate max-w-[280px]" title={e.error}>
                          {e.error}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {job.skipped_no_credentials_count > 0 && (
                <div className="text-xs text-slate-600">
                  ⏭️ {job.skipped_no_credentials_count} immobili saltati per credenziali mancanti
                  (vedi CSV).
                </div>
              )}
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
              <div className="flex gap-2 items-start">
                <X className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-rose-800">Job fallito</div>
                  <div className="text-rose-700">{job.fatal_error}</div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!job && (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Annulla
                </Button>
                {preview?.already_sent > 0 && (
                  <Button
                    variant="outline"
                    onClick={cleanupAndRelaunch}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid="cleanup-and-relaunch-btn"
                    title="Cancella submissions con 0 record (probabilmente fasulle dal bug) e rilancia"
                  >
                    🧹 Pulisci & Rilancia
                  </Button>
                )}
                <Button
                  onClick={launchBulkSend}
                  disabled={!preview || preview.sendable_properties === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="bulk-send-confirm-btn"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Avvia invio ({preview?.sendable_properties || 0} immobili)
                </Button>
              </>
            )}
            {isRunning && (
              <>
                <Button
                  variant="outline"
                  onClick={cancelJob}
                  disabled={job?.status === 'cancelling'}
                  className="text-rose-600 hover:bg-rose-50"
                  data-testid="bulk-send-cancel-btn"
                >
                  {job?.status === 'cancelling' ? 'Annullamento…' : '✋ Annulla invio'}
                </Button>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Chiudi (continua in background)
                </Button>
              </>
            )}
            {(isCompleted || job?.status === 'failed') && (
              <>
                {job?.errors_count > 0 && (
                  <Button variant="outline" onClick={downloadErrorsCsv}>
                    <Download className="h-4 w-4 mr-1" />
                    Esporta CSV errori
                  </Button>
                )}
                <Button onClick={() => { setOpen(false); setJob(null); }}>Chiudi</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick edit dialog (open from skipped property list) */}
      <QuickEditRoss1000Dialog
        propertyId={editingProp}
        open={!!editingProp}
        onClose={() => setEditingProp(null)}
        onSaved={() => {
          // Refresh preview after a property fix
          fetchPreview(selectedRegions);
        }}
      />
    </>
  );
};

export default IstatBulkSendButton;
